'use client';

import { JSFunction } from '@ahm/common-helpers';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAuth } from '@/providers/auth-provider';

interface UseMessengerOptions {
  onAuthRequired?: () => void;
  showLoginDialog?: (show: boolean) => void;
  onActionCompleted?: () => void;
}

interface PendingAction {
  pageId: number;
  customMessage?: string;
}

export function useMessenger(options: UseMessengerOptions = {}) {
  const isMobile = useIsMobile();
  const { user, isAuthenticated, login } = useAuth();
  const isWebinapp = useDetectWebInApp();
  const { onAuthRequired, showLoginDialog, onActionCompleted } = options;

  // Lưu trữ action pending để thực hiện sau khi đăng nhập
  const pendingActionRef = useRef<PendingAction | null>(null);

  const handleSendMessage = useCallback(
    (pageId: number, customMessage?: string) => {
      const messageText = encodeURIComponent(
        customMessage ?? 'Tôi muốn đặt đơn'
      );

      // Sử dụng URL khác nhau cho mobile và desktop
      const baseUrl = isMobile
        ? `http://m.me/${pageId}?text=${messageText}`
        : `https://www.messenger.com/t/${pageId}?text=${messageText}`;

      const baseRef = isWebinapp ? 'app' : 'web-slug';
      const refParam = user?.cunstomerId
        ? `${baseRef}-uid${user.cunstomerId}`
        : baseRef;

      const url = `${baseUrl}&ref=${refParam}`;

      if (!pageId) {
        toast.error('Có lỗi khi đặt đơn của cửa hàng này, vui lòng thử lại.');
        return;
      }

      if (isWebinapp) {
        try {
          JSFunction.call({
            name: 'openPage',
            body: url,
          });
        } catch (error) {
          window.open(url);
        }
      } else {
        window.open(url);
      }
    },
    [isWebinapp, user, isMobile]
  );

  useEffect(() => {
    if (isAuthenticated && pendingActionRef.current) {
      const { pageId, customMessage } = pendingActionRef.current;
      pendingActionRef.current = null;

      handleSendMessage(pageId, customMessage);

      if (onActionCompleted) {
        onActionCompleted();
      }
    }
  }, [isAuthenticated, handleSendMessage, onActionCompleted]);

  const handleChatClick = useCallback(
    (
      pageId: number,
      customMessage?: string,
      continueWithoutLogin?: boolean
    ) => {
      if (!isAuthenticated && !continueWithoutLogin) {
        pendingActionRef.current = { pageId, customMessage };

        if (showLoginDialog) {
          showLoginDialog(true);
        } else if (onAuthRequired) {
          onAuthRequired();
        } else {
          login().catch((error) => {
            pendingActionRef.current = null;
            toast.error(
              error.message || 'Đăng nhập thất bại, vui lòng thử lại.'
            );
          });
        }
        return;
      }

      if (pageId) {
        console.log(`Opening chat for page ID: ${pageId}`);
        handleSendMessage(pageId, customMessage);
      }
    },
    [isAuthenticated, handleSendMessage, showLoginDialog, onAuthRequired, login]
  );

  return {
    handleSendMessage,
    handleChatClick,
    isAuthenticated,
    clearPendingAction: () => {
      pendingActionRef.current = null;
    },
    hasPendingAction: () => pendingActionRef.current !== null,
  };
}
